// src/pages/rankings/RankingsPage.tsx
import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useSearchParams } from "react-router-dom";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase/config";
import { useTranslation } from "../../hooks/useTranslation";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import PublicHeader from "../../components/PublicHeader";
import { C, FONT } from "../../theme";
import { AGE_GROUP_DEFINITIONS, getWeightClassesForGroup } from "../../utils/rankingCalculator";
import { withCache } from "../../utils/cache";

// ── Responsive hook ────────────────────────────────────────────────────────────
function useWindowWidth() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return w;
}

// ── Types ──────────────────────────────────────────────────────────────────────
interface RankingEntry {
  id: string;
  athleteId: string;
  name: string;
  club: string;
  ageGroup: string;
  gender: string;
  weightClass: string;
  points: number;
  pointsPrevYear?: number;
  rank: number;
  year: number;
  imageUrl?: string;
}

// ── Constants ─────────────────────────────────────────────────────────────────
const CURRENT_YEAR = new Date().getFullYear();
// Keep published legacy seasons available while a new calendar season starts
// empty. No historical snapshot is rewritten or recalculated by this selector.
const YEAR_OPTIONS = Array.from(
  { length: Math.max(1, CURRENT_YEAR - 2024 + 1) },
  (_, index) => CURRENT_YEAR - index
);
const AGE_GROUPS = AGE_GROUP_DEFINITIONS.map(g => g.key);

// ── Icons ─────────────────────────────────────────────────────────────────────
const BackIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
const CalendarIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const TrophyIcon = () => (
  <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 9H4a2 2 0 0 1-2-2V5h4" /><path d="M18 9h2a2 2 0 0 0 2-2V5h-4" />
    <path d="M12 17v4" /><path d="M8 21h8" /><path d="M6 5v4a6 6 0 0 0 12 0V5H6z" />
  </svg>
);
const ChevronRightIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);
const EventsIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
const ClubIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);
const FilterIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);
const InfoIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);
const XIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// ── Athlete image / initials avatar
const AthleteImage = ({ firstName, lastName, imageUrl }: { firstName: string; lastName: string; imageUrl?: string }) => {
  const initials = `${(firstName ?? "").charAt(0)}${(lastName ?? "").charAt(0)}`.toUpperCase() || "?";
  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt=""
        style={{
          width: 34, height: 34, borderRadius: "50%",
          objectFit: "cover", flexShrink: 0,
          border: "2px solid var(--color-border-strong)"
        }}
      />
    );
  }
  return (
    <div style={{
      width: 34, height: 34, borderRadius: "50%",
      background: C.surface2,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: 12, fontWeight: 700, color: C.text2,
      fontFamily: FONT.body, flexShrink: 0
    }}>
      {initials}
    </div>
  );
};

// ── Medal component ────────────────────────────────────────────────────────────
function RankBadge({ rank }: { rank: number }) {
  if (rank === 1) {
    return (
      <div style={{
        width: 32, height: 32, borderRadius: "50%",
        background: "linear-gradient(135deg, #ffd700, #f59e0b)",
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 0 12px rgba(245,158,11,0.4)",
        fontSize: 12, fontWeight: 800, color: "#78350f",
        fontFamily: FONT.body, flexShrink: 0,
      }}>1</div>
    );
  }
  if (rank === 2) {
    return (
      <div style={{
        width: 32, height: 32, borderRadius: "50%",
        background: "linear-gradient(135deg, #e2e8f0, #94a3b8)",
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 0 10px rgba(148,163,184,0.3)",
        fontSize: 12, fontWeight: 800, color: "var(--color-text)",
        fontFamily: FONT.body, flexShrink: 0,
      }}>2</div>
    );
  }
  if (rank === 3) {
    return (
      <div style={{
        width: 32, height: 32, borderRadius: "50%",
        background: "linear-gradient(135deg, #fb923c, #c2410c)",
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 0 10px rgba(251,146,60,0.35)",
        fontSize: 12, fontWeight: 800, color: C.text,
        fontFamily: FONT.body, flexShrink: 0,
      }}>3</div>
    );
  }
  return (
    <div style={{
      width: 32, height: 32, borderRadius: "50%",
      background: C.surface2,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: 12, fontWeight: 700, color: C.text2,
      fontFamily: FONT.body, flexShrink: 0,
    }}>{rank}</div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function RankingsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const w = useWindowWidth();
  const isMobile = w < 640;
  const isTablet = w >= 640 && w < 1024;

  // Optional club-scope filter from ?club=NAME (set by ClubRankings rows).
  // Filtering happens client-side on the loaded snapshot so it's cheap and
  // doesn't change any Firestore reads.
  const clubFilter = searchParams.get("club") ?? "";
  const clearClubFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("club");
    setSearchParams(next, { replace: true });
  };

  const [heroLoaded, setHeroLoaded] = useState(false);

  // ── Filters
  const [selectedGender, setSelectedGender] = useState<"male" | "female">("male");
  const [selectedAgeGroup, setSelectedAgeGroup] = useState(AGE_GROUPS[AGE_GROUPS.length - 1] ?? "Seniors");
  const [selectedWeightClass, setSelectedWeightClass] = useState("all");
  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);
  const [athleteSearch, setAthleteSearch] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(!isMobile);

  // ── Data
  const [rankings, setRankings] = useState<RankingEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  // ── Weight classes for current selection
  const weightClasses = getWeightClassesForGroup(selectedAgeGroup, selectedGender);

  useEffect(() => { const t = setTimeout(() => setHeroLoaded(true), 80); return () => clearTimeout(t); }, []);

  // Reset weight class when group/gender changes
  useEffect(() => {
    setSelectedWeightClass("all");
  }, [selectedAgeGroup, selectedGender]);

  // Update filtersOpen based on screen size
  useEffect(() => {
    if (!isMobile) setFiltersOpen(true);
  }, [isMobile]);

  // ── Fetch rankings
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        // Key is under the `rankingSnapshot:` prefix so the bust in
        // recalculateAndSaveSnapshots (which calls
        // invalidateCachePrefix("rankingSnapshot:")) clears it too.
        // In club mode (?club=NAME) we load EVERY category for the year so the
        // page can show a club's FULL roster across all genders / age groups /
        // weight classes. Otherwise we load just the selected category (cheaper).
        const cacheKey = clubFilter
          ? `rankingSnapshot:public:${selectedYear}:club`
          : `rankingSnapshot:public:${selectedYear}:${selectedGender}:${selectedAgeGroup}:${selectedWeightClass}`;
        const data = await withCache<RankingEntry[]>(cacheKey, 5 * 60 * 1000, async () => {
          const constraints: any[] = [where("year", "==", selectedYear)];
          if (!clubFilter) {
            constraints.push(where("gender", "==", selectedGender));
            constraints.push(where("ageGroup", "==", selectedAgeGroup));
            if (selectedWeightClass !== "all") {
              constraints.push(where("weightClass", "==", selectedWeightClass));
            }
          }
          const authoritative = await getDocs(query(collection(db, "authoritativeRankingSnapshots"), ...constraints));
          // A finalized new-lifecycle edition is authoritative for its season.
          // Legacy snapshots remain the historical fallback and are never rewritten.
          const snap = authoritative.empty
            ? await getDocs(query(collection(db, "rankingSnapshots"), ...constraints))
            : authoritative;
          // Flatten the snapshot athletes array into individual ranking entries
          const entries: RankingEntry[] = [];
          snap.docs.forEach((d) => {
            const docData = d.data();
            const athletesArr = docData.athletes || [];
            athletesArr.forEach((ath: any) => {
              entries.push({
                id: d.id + "_" + ath.athleteId,
                athleteId: ath.athleteId,
                name: `${ath.firstName} ${ath.lastName}`,
                club: ath.club || "",
                ageGroup: docData.ageGroup || "",
                gender: docData.gender || "",
                weightClass: docData.weightClass || "",
                points: ath.currentYearPoints || 0,
                pointsPrevYear: ath.previousYearPoints || 0,
                imageUrl: ath.imageUrl || undefined,
                rank: ath.rank || 99,
                year: docData.year || 0,
              });
            });
          });
          if (clubFilter) {
            // Club roster: group by gender → age group → weight → rank.
            const ageIdx = (g: string) => {
              const i = AGE_GROUPS.indexOf(g);
              return i === -1 ? 999 : i;
            };
            entries.sort(
              (a, b) =>
                a.gender.localeCompare(b.gender) ||
                ageIdx(a.ageGroup) - ageIdx(b.ageGroup) ||
                a.weightClass.localeCompare(b.weightClass) ||
                a.rank - b.rank
            );
          } else {
            entries.sort((a, b) => a.weightClass.localeCompare(b.weightClass) || a.rank - b.rank);
          }
          return entries;
        });
        if (cancelled) return;
        setRankings(data);
        setHasFetched(true);
      } catch (e) {
        console.error("Failed to load rankings", e);
        if (!cancelled) { setRankings([]); setHasFetched(true); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [selectedYear, selectedGender, selectedAgeGroup, selectedWeightClass, clubFilter]);

  // ── Filtered results (client-side athlete search + club scope) ─────────
  const filtered = rankings.filter((r) => {
    if (clubFilter && (r.club ?? "").toLowerCase() !== clubFilter.toLowerCase()) {
      return false;
    }
    if (athleteSearch.trim()) {
      const q = athleteSearch.toLowerCase();
      const name = r.name.toLowerCase();
      const club = (r.club ?? "").toLowerCase();
      if (!name.includes(q) && !club.includes(q)) return false;
    }
    return true;
  });

  // Labels for the club-roster view, where rows span multiple categories.
  const ageLabel = (key: string) =>
    AGE_GROUP_DEFINITIONS.find((g) => g.key === key)?.label ?? key;
  const genderLabel = (g: string) =>
    g === "female" ? t("ranking.categories.girls") : t("ranking.categories.boys");
  const categoryLabel = (e: RankingEntry) =>
    `${genderLabel(e.gender)} · ${ageLabel(e.ageGroup)} · ${e.weightClass}`;

  const genderCategories = [
    { value: "male", labelKey: "ranking.categories.boys" },
    { value: "female", labelKey: "ranking.categories.girls" },
  ] as const;

  const activeFiltersCount = [
    selectedWeightClass !== "all",
    athleteSearch.trim() !== "",
    clubFilter !== "",
  ].filter(Boolean).length;

  return (
    <>
    <Helmet>
      <title>{t("seo.rankings.title")}</title>
      <meta name="description" content={t("seo.rankings.description")} />
      <link rel="canonical" href="https://registration.taekwondo.ge/rankings" />
      <meta property="og:title" content={t("seo.rankings.title")} />
      <meta property="og:description" content={t("seo.rankings.description")} />
      <meta property="og:url" content="https://registration.taekwondo.ge/rankings" />
      <meta property="og:type" content="website" />
      <meta property="og:image" content="https://registration.taekwondo.ge/geo.png" />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={t("seo.rankings.title")} />
      <meta name="twitter:description" content={t("seo.rankings.description")} />
      <meta name="twitter:image" content="https://registration.taekwondo.ge/geo.png" />
      <link rel="alternate" hrefLang="en" href="https://registration.taekwondo.ge/rankings" />
      <link rel="alternate" hrefLang="ka" href="https://registration.taekwondo.ge/rankings" />
      <link rel="alternate" hrefLang="x-default" href="https://registration.taekwondo.ge/rankings" />
    </Helmet>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Figtree:wght@400;500;600;700;800&display=swap');

        @keyframes heroBgZoom {
          from { transform: scale(1.07); }
          to   { transform: scale(1); }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes rowIn {
          from { opacity: 0; transform: translateX(-8px); }
          to   { opacity: 1; transform: translateX(0); }
        }

        .rk-loaded .rk-a0 { animation: fadeUp 0.65s cubic-bezier(.22,1,.36,1) 0.08s both; }
        .rk-loaded .rk-a1 { animation: fadeUp 0.65s cubic-bezier(.22,1,.36,1) 0.18s both; }
        .rk-loaded .rk-a2 { animation: fadeUp 0.65s cubic-bezier(.22,1,.36,1) 0.28s both; }
        .rk-loaded .rk-a3 { animation: fadeUp 0.65s cubic-bezier(.22,1,.36,1) 0.38s both; }

        .rk-back:hover { color: var(--color-text) !important; border-color: var(--color-text-subtle) !important; }
        .rk-nav-link:hover { color: var(--color-accent) !important; }
        .rk-nav-cta:hover { background: rgba(218,41,28,0.85) !important; }
        .rk-gender-tab:hover { background: var(--color-border) !important; }
        .rk-row:hover { background: var(--color-border) !important; }
        .rk-row-top:hover { background: rgba(218,41,28,0.06) !important; }
        .rk-profile-btn:hover { color: var(--color-text) !important; background: var(--color-surface-3) !important; }
        .rk-club-btn:hover { background: var(--color-border) !important; }
        .rk-filter-select:focus { border-color: var(--color-accent) !important; outline: none; }
        .rk-search-input:focus { border-color: var(--color-accent) !important; outline: none; box-shadow: var(--focus-ring) !important; }
        .rk-reset:hover { color: var(--color-text) !important; }
        .rk-events-link:hover { background: var(--color-border) !important; }
        .rk-age-btn:hover { border-color: var(--color-text-subtle) !important; }

        .rk-filter-select option {
          background: var(--color-surface);
          color: var(--color-text);
        }

        * { box-sizing: border-box; }
      `}</style>

      <div style={s.page}>

        {/* ══════════════ NAVBAR ══════════════════════════════════════════════ */}
        <PublicHeader backTo="/" backLabel={t("nav.backToHome")} />
        {false && <header style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          height: 56, padding: isMobile ? "0 14px" : "0 32px",
          backgroundColor: "rgba(7,10,15,0.95)",
          backdropFilter: "blur(12px)",
          borderBottom: `1px solid ${C.border}`,
          position: "sticky", top: 0, zIndex: 100,
        }}>
          <button
            className="rk-back"
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              background: "none", border: `1px solid ${C.border2}`,
              borderRadius: 8, padding: "5px 11px", cursor: "pointer",
              color: C.text2, fontFamily: FONT.body,
              fontSize: 12, fontWeight: 600, letterSpacing: "0.4px",
              transition: "color 0.15s, border-color 0.15s",
            }}
            onClick={() => navigate("/")}
          >
            <BackIcon />
            {!isMobile && t("common.back")}
          </button>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src="/geo.png" alt="Logo" style={{ width: 30, height: 30, objectFit: "contain" }} />
            {!isMobile && (
              <div>
                <div style={{ fontFamily: FONT.display, fontSize: 14, letterSpacing: "3px", color: C.text, lineHeight: 1.1 }}>GEORGIAN TAEKWONDO</div>
                <div style={{ fontFamily: FONT.display, fontSize: 9, letterSpacing: "2.5px", color: C.text3, lineHeight: 1 }}>NATIONAL FEDERATION</div>
              </div>
            )}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 6 : 10 }}>
            <button
              className="rk-nav-link"
              style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                background: "none", border: "none", cursor: "pointer",
                color: C.text2, fontSize: 12, fontWeight: 600,
                fontFamily: FONT.body, letterSpacing: "0.3px", transition: "color 0.15s", padding: "4px 6px",
              }}
              onClick={() => navigate("/events")}
            >
              <EventsIcon />
              {!isMobile && <span>{t("landing.events")}</span>}
            </button>
            <LanguageSwitcher />
            <button
              className="rk-nav-cta"
              style={{
                backgroundColor: C.red, color: C.onAccent,
                border: "none", borderRadius: 8, cursor: "pointer",
                fontFamily: FONT.body, fontSize: 12, fontWeight: 700,
                padding: isMobile ? "7px 12px" : "7px 16px",
                letterSpacing: "0.3px", transition: "background 0.15s",
              }}
              onClick={() => navigate("/login")}
            >
              {t("login.button")} →
            </button>
          </div>
        </header>}

        {/* ══════════════ HERO ════════════════════════════════════════════════ */}
        <div className={heroLoaded ? "rk-loaded" : ""} style={s.hero}>
          <div style={s.heroBgWrap}>
            <div style={{
              ...s.heroBg,
              animation: heroLoaded ? "heroBgZoom 1.8s cubic-bezier(.22,1,.36,1) forwards" : "none",
            }} />
            <div style={s.heroOverlay1} />
            <div style={s.heroOverlay2} />
            <div style={s.heroOverlay3} />
          </div>

          <div style={{
            ...s.heroContent,
            padding: isMobile ? "48px 20px 56px" : isTablet ? "56px 48px 64px" : "64px 80px 72px",
            alignItems: isMobile ? "center" : "flex-start",
            textAlign: isMobile ? "center" : "left",
          }}>
            <div className="rk-a0" style={s.heroEyebrow}>
              {t("ranking.subtitle")}
            </div>
            <h1 className="rk-a1" style={{
              ...s.heroTitle,
              fontSize: isMobile ? "clamp(44px,13vw,64px)" : isTablet ? "clamp(56px,8vw,78px)" : "clamp(64px,7vw,92px)",
            }}>
              {t("ranking.pageTitle")}
            </h1>
            <p className="rk-a2" style={{
              ...s.heroSub,
              maxWidth: isMobile ? "100%" : 440,
            }}>
              {t("ranking.tiedNote")}
            </p>

            {/* Quick nav pills */}
            <div className="rk-a3" style={{
              display: "flex",
              gap: 10,
              justifyContent: isMobile ? "center" : "flex-start",
              flexWrap: "wrap",
              marginTop: 4,
            }}>
              <button
                className="rk-events-link"
                style={s.heroPill}
                onClick={() => navigate("/events")}
              >
                <EventsIcon />
                <span>{t("landing.events")}</span>
              </button>
              <button
                className="rk-events-link"
                style={s.heroPill}
                onClick={() => navigate("/rankings/clubs")}
              >
                <ClubIcon />
                <span>{t("ranking.clubRankings.title")}</span>
              </button>
            </div>
          </div>
        </div>

        {/* ══════════════ GENDER TABS ═════════════════════════════════════════ */}
        <div style={{ ...s.genderTabsWrap, display: clubFilter ? "none" : undefined }}>
          <div style={{
            ...s.genderTabsInner,
            padding: isMobile ? "0 14px" : "0 32px",
          }}>
            {genderCategories.map(({ value, labelKey }) => (
              <button
                key={value}
                className="rk-gender-tab"
                style={{
                  ...s.genderTab,
                  ...(selectedGender === value ? s.genderTabActive : s.genderTabInactive),
                }}
                onClick={() => setSelectedGender(value)}
              >
                <span style={{
                  width: 7, height: 7, borderRadius: "50%",
                  backgroundColor: value === "male" ? "rgba(96,165,250,0.9)" : "rgba(236,72,153,0.9)",
                  display: "inline-block", flexShrink: 0,
                }} />
                {t(labelKey)}
              </button>
            ))}

            {/* Clubs link */}
            <button
              className="rk-club-btn"
              style={{
                ...s.clubsTabBtn,
                marginLeft: isMobile ? 0 : "auto",
                display: isMobile ? "none" : "inline-flex",
              }}
              onClick={() => navigate("/rankings/clubs")}
            >
              <ClubIcon />
              {t("ranking.clubRankings.title")}
              <ChevronRightIcon />
            </button>
          </div>
        </div>

        {/* ══════════════ FILTERS + TABLE LAYOUT ═════════════════════════════ */}
        <div style={{
          ...s.mainLayout,
          flexDirection: isMobile ? "column" : isTablet ? "column" : "row",
          padding: isMobile ? "20px 14px 64px" : isTablet ? "24px 24px 64px" : "32px 40px 72px",
          maxWidth: 1280,
          margin: "0 auto",
          gap: isMobile ? 16 : isTablet ? 20 : 28,
        }}>

          {/* ── Filter Panel ─────────────────────────────────────────────── */}
          <div style={{
            ...s.filterPanel,
            width: isMobile || isTablet ? "100%" : 260,
            flexShrink: 0,
          }}>
            {/* Filter header (mobile toggle) */}
            <button
              style={s.filterHeader}
              onClick={() => { if (isMobile) setFiltersOpen((p) => !p); }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <FilterIcon />
                <span style={s.filterTitle}>{t("ranking.filters.filters")}</span>
                {activeFiltersCount > 0 && (
                  <span style={s.activeFilterBadge}>{activeFiltersCount}</span>
                )}
              </div>
              {isMobile && (
                <svg
                  width="14" height="14" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
                  style={{ transform: filtersOpen ? "rotate(180deg)" : "none", transition: "transform 0.25s", color: C.text2 }}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              )}
            </button>

            {(filtersOpen || !isMobile) && (
              <div style={s.filterBody}>

                {/* Year */}
                <div style={s.filterGroup}>
                  <label style={s.filterLabel}>
                    <CalendarIcon />
                    {t("ranking.filters.year")}
                  </label>
                  <div style={s.yearBtnRow}>
                    {YEAR_OPTIONS.map((y) => (
                      <button
                        key={y}
                        style={{
                          ...s.yearBtn,
                          ...(selectedYear === y ? s.yearBtnActive : s.yearBtnInactive),
                        }}
                        onClick={() => setSelectedYear(y)}
                      >
                        {y}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Age Group */}
                <div style={{ ...s.filterGroup, display: clubFilter ? "none" : "flex" }}>
                  <label style={s.filterLabel}>{t("ranking.filters.ageGroup")}</label>
                                  <div style={s.ageGroupGrid}>
                    {AGE_GROUP_DEFINITIONS.map((g) => (
                      <button
                        key={g.key}
                        className="rk-age-btn"
                        style={{
                          ...s.ageGroupBtn,
                          ...(selectedAgeGroup === g.key ? s.ageGroupBtnActive : s.ageGroupBtnInactive),
                        }}
                        onClick={() => setSelectedAgeGroup(g.key)}
                      >
                        {g.label}
                      </button>
                    ))}
                </div>
                </div>

                {/* Weight Class */}
                <div style={{ ...s.filterGroup, display: clubFilter ? "none" : "flex" }}>
                  <label style={s.filterLabel}>{t("ranking.filters.subCategory")}</label>
                  <select
                    className="rk-filter-select"
                    style={s.filterSelect}
                    value={selectedWeightClass}
                    onChange={(e) => setSelectedWeightClass(e.target.value)}
                  >
                    <option value="all">{t("ranking.filters.all")}</option>
                    {weightClasses.map((wc) => (
                      <option key={wc} value={wc}>{wc}</option>
                    ))}
                  </select>
                </div>

                {/* Athlete search */}
                <div style={s.filterGroup}>
                  <label style={s.filterLabel}>{t("ranking.filters.athlete")}</label>
                  <div style={s.searchWrap}>
                    <span style={s.searchIcon}><SearchIcon /></span>
                    <input
                      className="rk-search-input"
                      style={s.searchInput}
                      type="text"
                      value={athleteSearch}
                      onChange={(e) => setAthleteSearch(e.target.value)}
                      placeholder={t("ranking.filters.athletePlaceholder")}
                    />
                    {athleteSearch && (
                      <button
                        style={s.searchClear}
                        onClick={() => setAthleteSearch("")}
                        title="Clear"
                      >
                        <XIcon />
                      </button>
                    )}
                  </div>
                </div>

                {/* Reset */}
                {activeFiltersCount > 0 && (
                  <button
                    className="rk-reset"
                    style={s.resetBtn}
                    onClick={() => {
                      setSelectedWeightClass("all");
                      setAthleteSearch("");
                      if (clubFilter) clearClubFilter();
                    }}
                  >
                    <XIcon />
                    {t("ranking.filters.reset")}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* ── Rankings Table ───────────────────────────────────────────── */}
          <div style={{ flex: 1, minWidth: 0 }}>

            {/* Club-scope banner — appears when navigated from ClubRankings. */}
            {clubFilter && (
              <div
                role="status"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: isMobile ? "10px 14px" : "12px 16px",
                  marginBottom: 14,
                  backgroundColor: "rgba(218,41,28,0.08)",
                  border: "1px solid rgba(218,41,28,0.35)",
                  borderRadius: 10,
                  flexWrap: "wrap",
                }}
              >
                <span style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 13,
                  color: C.danger,
                  fontFamily: FONT.body,
                  fontWeight: 600,
                  letterSpacing: "0.2px",
                }}>
                  Showing all ranked athletes from <strong style={{ color: C.text }}>{clubFilter}</strong> · {selectedYear} · across every category
                </span>
                <button
                  onClick={clearClubFilter}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    background: C.surface2,
                    color: C.text,
                    border: "1px solid var(--color-border-strong)",
                    borderRadius: 6,
                    padding: "5px 10px",
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: "pointer",
                    fontFamily: FONT.body,
                    letterSpacing: "0.3px",
                  }}
                >
                  <XIcon /> Clear club filter
                </button>
              </div>
            )}

            {/* Table header */}
            <div style={{
              ...s.tableHeader,
              flexDirection: isMobile ? "column" : "row",
              gap: isMobile ? 8 : 0,
              alignItems: isMobile ? "flex-start" : "center",
              marginBottom: 14,
            }}>
              <div>
                <div style={s.tableTitle}>
                  {clubFilter
                    ? clubFilter
                    : `${selectedGender === "male" ? t("ranking.categories.boys") : t("ranking.categories.girls")} · ${AGE_GROUP_DEFINITIONS.find(g => g.key === selectedAgeGroup)?.label ?? selectedAgeGroup}${selectedWeightClass !== "all" ? ` · ${selectedWeightClass}` : ""}`}
                </div>
                <div style={s.tableSubtitle}>{selectedYear} {t("ranking.filters.year")}</div>
              </div>
              {!loading && hasFetched && (
                <div style={s.tableCount}>
                  {filtered.length} {filtered.length === 1 ? "athlete" : "athletes"}
                </div>
              )}
            </div>

            {/* Tied note banner */}
            {!loading && hasFetched && rankings.length > 0 && (
              <div style={s.tiedNoteBanner}>
                <InfoIcon />
                <span>{t("ranking.tiedNote")}</span>
              </div>
            )}

            {/* Loading skeleton rows */}
            {loading && (
              <div style={s.tableCard}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} style={s.skeletonRow}>
                    <div style={{ ...s.skeletonCircle, opacity: 1 / i }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ ...s.skeletonLine, width: "40%", marginBottom: 6 }} />
                      <div style={{ ...s.skeletonLine, width: "25%" }} />
                    </div>
                    <div style={{ ...s.skeletonLine, width: 50, flexShrink: 0 }} />
                  </div>
                ))}
              </div>
            )}

            {/* Empty state */}
            {!loading && hasFetched && filtered.length === 0 && (
              <div style={s.emptyState}>
                <div style={s.emptyIcon}><TrophyIcon /></div>
                <div style={s.emptyTitle}>{t("ranking.empty")}</div>
                <div style={s.emptyText}>
                  Try adjusting the filters or selecting a different year.
                </div>
              </div>
            )}

            {/* Rankings table */}
            {!loading && filtered.length > 0 && (
              <div style={s.tableCard}>
                {/* Table head */}
                {!isMobile && (
                  <div style={s.tableHeadRow}>
                    <div style={{ ...s.th, width: 60 }}>{t("ranking.table.rank")}</div>
                    <div style={{ ...s.th, flex: 1 }}>{t("ranking.table.name")}</div>
                    <div style={{ ...s.th, minWidth: 100, display: isTablet ? "none" : "block" }}>{t("ranking.table.club")}</div>
                    {selectedWeightClass === "all" && (
                      <div style={{ ...s.th, minWidth: 80, display: isMobile ? "none" : "block" }}>{t("ranking.table.weight")}</div>
                    )}
                    <div style={{ ...s.th, minWidth: 80, textAlign: "right" }}>{t("ranking.table.points")}</div>
                    <div style={{ ...s.th, width: 70, textAlign: "right" }}>{t("ranking.table.details")}</div>
                  </div>
                )}

                {/* Table rows */}
                {filtered.map((entry, idx) => {
                  const isTop = entry.rank <= 3;
                  return (
                    <div
                      key={entry.id}
                      className={isTop ? "rk-row-top" : "rk-row"}
                      style={{
                        ...s.tableRow,
                        ...(isTop ? s.tableRowTop : {}),
                        ...(idx === filtered.length - 1 ? { borderBottom: "none" } : {}),
                        animation: `rowIn 0.4s ease both ${Math.min(idx * 0.03, 0.5)}s`,
                      }}
                    >
                      {/* Mobile: card layout */}
                      {isMobile ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, flexWrap: "wrap" }}>
                          <RankBadge rank={entry.rank} />
                          <AthleteImage
                            firstName={entry.name.split(" ")[0]}
                            lastName={entry.name.split(" ").slice(1).join(" ")}
                            imageUrl={entry.imageUrl}
                          />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={s.athleteName} title={entry.name}>{entry.name}</div>
                            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 2 }}>
                              {clubFilter ? (
                                <span style={s.weightPill}>{categoryLabel(entry)}</span>
                              ) : (
                                <>
                                  {entry.club && <span style={s.clubPill}>{entry.club}</span>}
                                  {selectedWeightClass === "all" && entry.weightClass && (
                                    <span style={s.weightPill}>{entry.weightClass}</span>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3, flexShrink: 0 }}>
                            <div style={s.pointsVal}>{entry.points}</div>
                            <button
                              className="rk-profile-btn"
                              style={s.profileBtn}
                              onClick={() => navigate(`/rankings/athlete/${entry.athleteId}`)}
                              title={t("publicDirectory.viewProfile")}
                              aria-label={`${t("publicDirectory.viewProfile")}: ${entry.name}`}
                            >
                              {t("ranking.table.details")}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div style={{ width: 92, flexShrink: 0, display: "flex", alignItems: "center", gap: 8 }}>
                            <RankBadge rank={entry.rank} />
                            <AthleteImage
                              firstName={entry.name.split(" ")[0]}
                              lastName={entry.name.split(" ").slice(1).join(" ")}
                              imageUrl={entry.imageUrl}
                            />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={s.athleteName}>{entry.name}</div>
                            {clubFilter && (
                              <div style={{ fontSize: 11, color: C.text2, marginTop: 2, fontFamily: FONT.body }}>
                                {categoryLabel(entry)}
                              </div>
                            )}
                            {isTablet && entry.club && (
                              <div style={{ fontSize: 11, color: C.text3, marginTop: 2, fontFamily: FONT.body }}>
                                {entry.club}
                              </div>
                            )}
                          </div>
                          {!isTablet && (
                            <div style={{ minWidth: 100, fontSize: 12, color: C.text2, fontFamily: FONT.body }}>
                              {entry.club || "—"}
                            </div>
                          )}
                          {selectedWeightClass === "all" && (
                            <div style={{ minWidth: 80 }}>
                              <span style={s.weightPill}>{entry.weightClass}</span>
                            </div>
                          )}
                          <div style={{ minWidth: 80, textAlign: "right" }}>
                            <span style={s.pointsVal}>{entry.points}</span>
                            {entry.pointsPrevYear !== undefined && entry.pointsPrevYear > 0 && (
                              <div style={s.prevYearPts}>({entry.pointsPrevYear} prev)</div>
                            )}
                          </div>
                          <div style={{ width: 70, textAlign: "right" }}>
                            <button
                              className="rk-profile-btn"
                              style={s.profileBtn}
                              onClick={() => navigate(`/rankings/athlete/${entry.athleteId}`)}
                              title={t("publicDirectory.viewProfile")}
                              aria-label={`${t("publicDirectory.viewProfile")}: ${entry.name}`}
                            >
                              {t("ranking.table.details")}
                              <ChevronRightIcon />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Club rankings CTA */}
            {!loading && hasFetched && (
              <div style={{ marginTop: 20 }}>
                <button
                  className="rk-club-btn"
                  style={s.clubRankingsCta}
                  onClick={() => navigate("/rankings/clubs")}
                >
                  <ClubIcon />
                  <div>
                    <div style={s.ctaTitle}>{t("ranking.clubRankings.title")}</div>
                    <div style={s.ctaSub}>{t("ranking.clubRankings.subtitle")}</div>
                  </div>
                  <ChevronRightIcon />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ══════════════ FOOTER ══════════════════════════════════════════════ */}
        <footer style={{
          ...s.footer,
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? 14 : 0,
          padding: isMobile ? "22px 20px" : "18px 40px",
          textAlign: isMobile ? "center" : "left",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src="/geo.png" alt="Logo" style={{ height: 20, opacity: 0.35, objectFit: "contain" }} />
            <span style={s.footerText}>© {new Date().getFullYear()} Georgian Taekwondo National Federation</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button style={s.footerLink} onClick={() => navigate("/")}>Home</button>
            <span style={s.footerDot}>·</span>
            <button style={s.footerLink} onClick={() => navigate("/events")}>{t("landing.events")}</button>
            <span style={s.footerDot}>·</span>
            <button style={s.footerLink} onClick={() => navigate("/privacy")}>{t("landing.privacy")}</button>
          </div>
        </footer>
      </div>
    </>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────
const s: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    backgroundColor: C.bg,
    fontFamily: FONT.body,
  },

  // ── Navbar
  navbar: {
    position: "sticky",
    top: 0,
    zIndex: 100,
    height: 58,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "rgba(7,10,15,0.92)",
    backdropFilter: "blur(16px)",
    WebkitBackdropFilter: "blur(16px)",
    borderBottom: `1px solid ${C.border}`,
  },
  backBtn: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    background: "none",
    border: `1px solid ${C.border2}`,
    borderRadius: 8,
    color: C.text2,
    cursor: "pointer",
    padding: "6px 10px",
    transition: "color 0.15s, border-color 0.15s",
    fontFamily: FONT.body,
    flexShrink: 0,
  },
  navCenter: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    position: "absolute",
    left: "50%",
    transform: "translateX(-50%)",
  },
  navLogo: {
    height: 28,
    objectFit: "contain",
    opacity: 1,
  },
  navBrandTop: {
    fontSize: 11,
    fontWeight: 800,
    color: C.text,
    letterSpacing: "1.5px",
    lineHeight: 1.2,
    fontFamily: FONT.body,
  },
  navBrandBot: {
    fontSize: 9,
    fontWeight: 600,
    color: C.text2,
    letterSpacing: "1px",
    lineHeight: 1.2,
    fontFamily: FONT.body,
  },
  navTextLink: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    background: "none",
    border: "none",
    color: C.text2,
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 600,
    fontFamily: FONT.body,
    letterSpacing: "0.3px",
    padding: "4px 8px",
    transition: "color 0.15s",
  },
  navCta: {
    padding: "7px 14px",
    backgroundColor: C.red,
    color: C.onAccent,
    border: "none",
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: FONT.body,
    letterSpacing: "0.3px",
    transition: "background 0.15s",
    whiteSpace: "nowrap",
  },

  // ── Hero
  hero: {
    position: "relative",
    overflow: "hidden",
    minHeight: 272,
  },
  heroBgWrap: { position: "absolute", inset: 0, overflow: "hidden" },
  heroBg: {
    position: "absolute",
    inset: 0,
    backgroundImage: "url('/hero-bg.jpg')",
    backgroundSize: "cover",
    backgroundPosition: "center 30%",
    willChange: "transform",
  },
  heroOverlay1: {
    position: "absolute", inset: 0,
    background: "linear-gradient(135deg, rgba(7,10,15,0.93) 0%, rgba(7,10,15,0.55) 100%)",
  },
  heroOverlay2: {
    position: "absolute", inset: 0,
    background: "linear-gradient(0deg, var(--color-bg) 0%, transparent 65%)",
  },
  heroOverlay3: {
    position: "absolute", inset: 0,
    background: "radial-gradient(ellipse at 80% 50%, rgba(59,130,246,0.08) 0%, transparent 60%)",
  },
  heroContent: {
    position: "relative",
    zIndex: 2,
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  heroEyebrow: {
    fontSize: 10,
    fontWeight: 800,
    color: C.text2,
    letterSpacing: "3px",
    fontFamily: FONT.body,
  },
  heroTitle: {
    fontFamily: FONT.display,
    color: C.heroText,
    lineHeight: 0.95,
    letterSpacing: "1px",
    margin: 0,
  },
  heroSub: {
    fontSize: 13,
    color: C.heroText2,
    lineHeight: 1.6,
    fontFamily: FONT.body,
    margin: 0,
  },
  heroPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    padding: "7px 14px",
    background: C.surface2,
    border: `1px solid ${C.border}`,
    borderRadius: 40,
    color: C.text2,
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: FONT.body,
    transition: "background 0.15s",
  },

  // ── Gender tabs
  genderTabsWrap: {
    backgroundColor: C.surface2,
    borderBottom: `1px solid ${C.border}`,
    position: "sticky",
    top: 58,
    zIndex: 90,
  },
  genderTabsInner: {
    maxWidth: 1280,
    margin: "0 auto",
    display: "flex",
    alignItems: "center",
  },
  genderTab: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "14px 18px",
    border: "none",
    background: "none",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: FONT.body,
    borderBottom: "2px solid transparent",
    marginBottom: -1,
    transition: "color 0.15s, border-color 0.15s, background 0.15s",
    letterSpacing: "0.3px",
    whiteSpace: "nowrap",
  },
  genderTabActive: {
    color: C.text,
    borderBottom: `2px solid ${C.red}`,
  },
  genderTabInactive: {
    color: C.text3,
  },
  clubsTabBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "7px 14px",
    background: C.surface2,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    color: C.text2,
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 600,
    fontFamily: FONT.body,
    letterSpacing: "0.3px",
    transition: "background 0.15s",
    whiteSpace: "nowrap",
  },

  // ── Main layout
  mainLayout: {
    display: "flex",
    gap: 28,
    alignItems: "flex-start",
  },

  // ── Filter panel
  filterPanel: {
    backgroundColor: C.surface,
    borderRadius: 14,
    border: `1px solid ${C.border}`,
    overflow: "hidden",
  },
  filterHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 16px",
    background: "none",
    border: "none",
    cursor: "pointer",
    width: "100%",
    borderBottom: `1px solid ${C.border}`,
    fontFamily: FONT.body,
  },
  filterTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: C.text2,
    letterSpacing: "1.5px",
    textTransform: "uppercase" as const,
    fontFamily: FONT.body,
  },
  activeFilterBadge: {
    backgroundColor: C.red,
    color: C.onAccent,
    borderRadius: 20,
    fontSize: 10,
    fontWeight: 700,
    padding: "1px 7px",
    fontFamily: FONT.body,
  },
  filterBody: {
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: 18,
  },
  filterGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  filterLabel: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 10,
    fontWeight: 700,
    color: C.text3,
    letterSpacing: "1px",
    textTransform: "uppercase" as const,
    fontFamily: FONT.body,
  },
  yearBtnRow: {
    display: "flex",
    gap: 6,
  },
  yearBtn: {
    flex: 1,
    padding: "7px 4px",
    border: "1px solid transparent",
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: FONT.body,
    letterSpacing: "0.3px",
    transition: "all 0.15s",
  },
  yearBtnActive: {
    backgroundColor: C.red,
    color: C.onAccent,
    border: "1px solid #da291c",
  },
  yearBtnInactive: {
    backgroundColor: C.surface2,
    color: C.text2,
    border: `1px solid ${C.border}`,
  },
  ageGroupGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: 6,
  },
  ageGroupBtn: {
    padding: "7px 6px",
    border: "1px solid transparent",
    borderRadius: 8,
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: FONT.body,
    transition: "all 0.15s",
    letterSpacing: "0.3px",
  },
  ageGroupBtnActive: {
    backgroundColor: "rgba(218,41,28,0.15)",
    color: C.red,
    border: "1px solid rgba(218,41,28,0.35)",
  },
  ageGroupBtnInactive: {
    backgroundColor: C.surface2,
    color: C.text2,
    border: "1px solid var(--color-border)",
  },
  filterSelect: {
    padding: "9px 12px",
    backgroundColor: C.surface2,
    border: `1px solid ${C.border2}`,
    borderRadius: 9,
    color: C.text,
    fontSize: 13,
    fontFamily: FONT.body,
    width: "100%",
    cursor: "pointer",
    transition: "border-color 0.15s",
    appearance: "none" as const,
    WebkitAppearance: "none" as const,
  } as React.CSSProperties,
  searchWrap: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  searchIcon: {
    position: "absolute",
    left: 11,
    color: C.text3,
    display: "flex",
    alignItems: "center",
    pointerEvents: "none",
  },
  searchInput: {
    width: "100%",
    padding: "9px 36px",
    backgroundColor: C.surface2,
    border: `1px solid ${C.border2}`,
    borderRadius: 9,
    color: C.text,
    fontSize: 13,
    fontFamily: FONT.body,
    transition: "border-color 0.15s, box-shadow 0.15s",
  },
  searchClear: {
    position: "absolute",
    right: 10,
    background: "none",
    border: "none",
    color: C.text3,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    padding: 2,
  },
  resetBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    padding: "8px",
    background: "rgba(218,41,28,0.1)",
    border: "1px solid rgba(218,41,28,0.2)",
    borderRadius: 8,
    color: C.red,
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: FONT.body,
    transition: "color 0.15s",
    width: "100%",
  },

  // ── Table section
  tableHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  tableTitle: {
    fontFamily: FONT.display,
    fontSize: 22,
    color: C.text,
    letterSpacing: "0.5px",
    lineHeight: 1.1,
  },
  tableSubtitle: {
    fontSize: 11,
    color: C.text3,
    fontFamily: FONT.body,
    marginTop: 2,
    letterSpacing: "0.5px",
  },
  tableCount: {
    fontSize: 11,
    fontWeight: 700,
    color: C.text3,
    backgroundColor: C.surface2,
    border: `1px solid ${C.border}`,
    borderRadius: 20,
    padding: "3px 11px",
    fontFamily: FONT.body,
    letterSpacing: "0.5px",
    whiteSpace: "nowrap",
  },
  tiedNoteBanner: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "9px 14px",
    backgroundColor: "rgba(59,130,246,0.07)",
    border: "1px solid rgba(59,130,246,0.15)",
    borderRadius: 9,
    color: "rgba(96,165,250,0.75)",
    fontSize: 11,
    fontFamily: FONT.body,
    lineHeight: 1.5,
    marginBottom: 12,
  },
  tableCard: {
    backgroundColor: C.surface,
    borderRadius: 14,
    border: `1px solid ${C.border}`,
    overflow: "hidden",
  },
  tableHeadRow: {
    display: "flex",
    alignItems: "center",
    padding: "10px 16px",
    borderBottom: `1px solid ${C.border}`,
    backgroundColor: "var(--color-surface-2)",
  },
  th: {
    fontSize: 9,
    fontWeight: 800,
    color: C.text3,
    textTransform: "uppercase" as const,
    letterSpacing: "1px",
    fontFamily: FONT.body,
  },
  tableRow: {
    display: "flex",
    alignItems: "center",
    padding: "12px 16px",
    borderBottom: `1px solid ${C.border}`,
    gap: 12,
    transition: "background 0.15s",
    cursor: "default",
  },
  tableRowTop: {
    backgroundColor: "rgba(218,41,28,0.03)",
  },
  athleteName: {
    fontSize: 14,
    fontWeight: 700,
    color: C.text,
    fontFamily: FONT.body,
    lineHeight: 1.2,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  clubPill: {
    fontSize: 10,
    padding: "2px 8px",
    backgroundColor: C.surface2,
    color: C.text2,
    borderRadius: 20,
    fontFamily: FONT.body,
    fontWeight: 600,
    border: `1px solid ${C.border}`,
    whiteSpace: "nowrap",
  },
  weightPill: {
    fontSize: 10,
    padding: "2px 8px",
    backgroundColor: "rgba(59,130,246,0.1)",
    color: "rgba(96,165,250,0.8)",
    borderRadius: 20,
    fontFamily: FONT.body,
    fontWeight: 600,
    border: "1px solid rgba(59,130,246,0.2)",
    whiteSpace: "nowrap",
  },
  pointsVal: {
    fontSize: 18,
    fontWeight: 800,
    color: C.red,
    fontFamily: FONT.display,
    letterSpacing: "0.5px",
    lineHeight: 1,
  },
  prevYearPts: {
    fontSize: 9,
    color: C.text3,
    fontFamily: FONT.body,
    marginTop: 2,
    textAlign: "right",
  },
  profileBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    background: "none",
    border: `1px solid ${C.border}`,
    borderRadius: 7,
    padding: "4px 9px",
    fontSize: 10,
    fontWeight: 700,
    color: C.text3,
    cursor: "pointer",
    fontFamily: FONT.body,
    letterSpacing: "0.3px",
    transition: "color 0.15s, background 0.15s",
    whiteSpace: "nowrap",
  },

  // ── Club rankings CTA
  clubRankingsCta: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    padding: "16px 18px",
    backgroundColor: C.surface2,
    border: `1px solid ${C.border}`,
    borderRadius: 12,
    cursor: "pointer",
    width: "100%",
    textAlign: "left",
    transition: "background 0.15s",
    color: C.text2,
    fontFamily: FONT.body,
  },
  ctaTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: C.text,
    fontFamily: FONT.body,
    lineHeight: 1.2,
    marginBottom: 2,
  },
  ctaSub: {
    fontSize: 11,
    color: C.text3,
    fontFamily: FONT.body,
    lineHeight: 1.3,
  },

  // ── Skeleton
  skeletonRow: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    padding: "14px 16px",
    borderBottom: `1px solid ${C.border}`,
  },
  skeletonCircle: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    background: C.surface2,
    flexShrink: 0,
  },
  skeletonLine: {
    height: 10,
    borderRadius: 6,
    background: "linear-gradient(90deg, var(--color-border) 25%, var(--color-border-strong) 50%, var(--color-border) 75%)",
    backgroundSize: "200% 100%",
    animation: "shimmer 1.4s infinite",
  },

  // ── Empty state
  emptyState: {
    textAlign: "center",
    padding: "72px 20px",
    backgroundColor: C.surface,
    borderRadius: 14,
    border: `1px solid ${C.border}`,
  },
  emptyIcon: {
    color: "var(--color-border)",
    marginBottom: 14,
    display: "flex",
    justifyContent: "center",
  },
  emptyTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: C.text2,
    fontFamily: FONT.body,
    marginBottom: 6,
  },
  emptyText: {
    fontSize: 13,
    color: C.text3,
    fontFamily: FONT.body,
  },

  // ── Footer
  footer: {
    backgroundColor: C.bg,
    borderTop: `1px solid ${C.border}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap" as const,
  },
  footerText: {
    fontSize: 11,
    color: C.text3,
    fontFamily: FONT.body,
  },
  footerLink: {
    background: "none",
    border: "none",
    color: C.text3,
    fontSize: 11,
    cursor: "pointer",
    fontFamily: FONT.body,
    padding: 0,
    transition: "color 0.15s",
  },
  footerDot: {
    color: "var(--color-border-strong)",
    fontSize: 11,
  },
};
